import { ContentPageId } from '@shared/models';

export type MigratedPages = Extract<
  ContentPageId,
  | ContentPageId.minoroverview
  | ContentPageId.transferto
  | ContentPageId.transferfrom
  | ContentPageId.termsnew
  | ContentPageId.contactinfo
  | ContentPageId.employment
  | ContentPageId.personalinfo
  | ContentPageId.termsexisting
  | ContentPageId.incomenetworth
  | ContentPageId.review
  | ContentPageId.investmentobjectives
  | ContentPageId.confirm
  | ContentPageId.giftingrecipient
  | ContentPageId.termsgifting
  | ContentPageId.giftingconfirm
  | ContentPageId.giftingtriage
  | ContentPageId.giftingassets
  | ContentPageId.giftingrecipienttriage
  | ContentPageId.giftingsummary
  | ContentPageId.giftingmethod
>;

export const migratedPages = {
  [ContentPageId.minoroverview]: ContentPageId.minoroverview,
  [ContentPageId.transferto]: ContentPageId.transferto,
  [ContentPageId.employment]: ContentPageId.employment,
  [ContentPageId.personalinfo]: ContentPageId.personalinfo,
  [ContentPageId.transferfrom]: ContentPageId.transferfrom,
  [ContentPageId.termsnew]: ContentPageId.termsnew,
  [ContentPageId.contactinfo]: ContentPageId.contactinfo,
  [ContentPageId.termsexisting]: ContentPageId.termsexisting,
  [ContentPageId.incomenetworth]: ContentPageId.incomenetworth,
  [ContentPageId.review]: ContentPageId.review,
  [ContentPageId.investmentobjectives]: ContentPageId.investmentobjectives,
  [ContentPageId.confirm]: ContentPageId.confirm,
  [ContentPageId.giftingrecipient]: ContentPageId.giftingrecipient,
  [ContentPageId.termsgifting]: ContentPageId.termsgifting,
  [ContentPageId.giftingconfirm]: ContentPageId.giftingconfirm,
  [ContentPageId.giftingtriage]: ContentPageId.giftingtriage,
  [ContentPageId.giftingassets]: ContentPageId.giftingassets,
  [ContentPageId.giftingrecipienttriage]: ContentPageId.giftingrecipienttriage,
  [ContentPageId.giftingsummary]: ContentPageId.giftingsummary,
  [ContentPageId.giftingmethod]: ContentPageId.giftingmethod,
} as const;

interface AEMPageConfig<T extends MigratedPages> {
  interfaceName: `${Capitalize<T>}RemoteContent`;
  urls: {
    prod: string;
    test: string;
  };
}

type AEMConfig = {
  [K in MigratedPages]: AEMPageConfig<K>;
};

export const config: Readonly<AEMConfig> = {
  [ContentPageId.minoroverview]: {
    interfaceName: 'MinoroverviewRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/minorOverviewPageContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/minorOverviewPageContainer',
    },
  },
  [ContentPageId.transferto]: {
    interfaceName: 'TransfertoRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/minorTransferToFormContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/minorTransferToFormContainer',
    },
  },
  [ContentPageId.employment]: {
    interfaceName: 'EmploymentRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/minorOpenAccountEmploymentInfoFormContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/minorOpenAccountEmploymentInfoFormContainer',
    },
  },
  [ContentPageId.personalinfo]: {
    interfaceName: 'PersonalinfoRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/minorOpenAccountVerifyPersonalInfoFormContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/minorOpenAccountVerifyPersonalInfoFormContainer',
    },
  },
  [ContentPageId.transferfrom]: {
    interfaceName: 'TransferfromRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/minorTransferFromFormContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/minorTransferFromFormContainer',
    },
  },
  [ContentPageId.termsnew]: {
    interfaceName: 'TermsnewRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/onb-shared-content/coo-minor-esign-terms',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/onb-shared-content/coo-minor-esign-terms',
    },
  },
  [ContentPageId.contactinfo]: {
    interfaceName: 'ContactinfoRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/minorOpenAccountContactInfoFormContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/minorOpenAccountContactInfoFormContainer',
    },
  },
  [ContentPageId.termsexisting]: {
    interfaceName: 'TermsexistingRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/onb-shared-content/coo-minor-esign-terms',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/onb-shared-content/coo-minor-esign-terms',
    },
  },
  [ContentPageId.incomenetworth]: {
    interfaceName: 'IncomenetworthRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/minorOpenAccountIncomeNetworthFormContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/minorOpenAccountIncomeNetworthFormContainer',
    },
  },
  [ContentPageId.review]: {
    interfaceName: 'ReviewRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/minorReviewTransferPageContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/minorReviewTransferPageContainer',
    },
  },
  [ContentPageId.investmentobjectives]: {
    interfaceName: 'InvestmentobjectivesRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/minorOpenAccountInvestingStyleFormContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/minorOpenAccountInvestingStyleFormContainer',
    },
  },
  [ContentPageId.confirm]: {
    interfaceName: 'ConfirmRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/minorConfirmationPageContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/minorConfirmationPageContainer',
    },
  },
  [ContentPageId.giftingrecipient]: {
    interfaceName: 'GiftingrecipientRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/giftingRecipientFormContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/giftingRecipientFormContainer',
    },
  },

  [ContentPageId.termsgifting]: {
    interfaceName: 'TermsgiftingRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/giftingEsignFormContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/giftingEsignFormContainer',
    },
  },
  [ContentPageId.giftingconfirm]: {
    interfaceName: 'GiftingconfirmRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/giftingConfirmationPageContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/giftingConfirmationPageContainer',
    },
  },
  [ContentPageId.giftingtriage]: {
    interfaceName: 'GiftingtriageRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/giftingTriageFormContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/giftingTriageFormContainer',
    },
  },
  [ContentPageId.giftingassets]: {
    interfaceName: 'GiftingassetsRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/giftingAssetsFormContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/giftingAssetsFormContainer',
    },
  },
  [ContentPageId.giftingrecipienttriage]: {
    interfaceName: 'GiftingrecipienttriageRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/giftingRecipientTriageFormContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/giftingRecipientTriageFormContainer',
    },
  },
  [ContentPageId.giftingsummary]: {
    interfaceName: 'GiftingsummaryRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/giftingSummaryPageContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/giftingSummaryPageContainer',
    },
  },
  [ContentPageId.giftingmethod]: {
    interfaceName: 'GiftingmethodRemoteContent',
    urls: {
      prod: 'https://pi.aemgraphql.vanguard.com/graphql/execute.json/change-of-ownership/giftingMethodFormContainer',
      test: 'https://pi.aemgraphqltest.vanguard.com/graphql/execute.json/change-of-ownership/giftingMethodFormContainer',
    },
  },
};
